import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  FaCheck, FaClock, FaTimesCircle, FaFileAlt, FaSpinner, 
  FaArrowLeft, FaTrash, FaPrint, FaUser, FaWallet, FaBriefcase, 
  FaCheckCircle, FaTimes, FaMapMarkerAlt, FaCalendarAlt
} from "react-icons/fa";
import { supabase } from "../lib/supabase"; 
const C = {
  bg: "#FFFFFF",
  card: "#FFFFFF",
  primary: "#72ba06",                          // ⭐ NEW GREEN
  primaryHover: "#5f9c05",
  primaryLight: "rgba(114, 186, 6, 0.14)",
  text: "#0F1A05",                             // slightly greener-dark
  muted: "#4A5A2E",
  line: "#E5E7EB",
  ok: "#72ba06",                               // ⭐ GREEN
  okBg: "rgba(114, 186, 6, 0.18)",
  pending: "#F59E0B",
  pendingBg: "#FFFBEB",
  danger: "#EF4444",
  dangerBg: "#FEF2F2",
  link: "#3B82F6",
};
const getNormalizedStatus = (status) => {
  if (status === "Under Review" || status === "Pending") return "Shortlisted";
  return status;
};

export default function TrackApplication() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const urlRef = params.get("ref"); 
  
  const [applications, setApplications] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [urlRef]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      setError("You must be logged in to track applications.");
      setLoading(false);
      return;
    }

    const { data: dbApps, error: dbError } = await supabase
      .from('kyc_applications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (dbError) {
      setError("Could not fetch applications.");
    } else {
      setApplications(dbApps || []);
      
      if (urlRef) {
        const foundApp = dbApps.find(app => app.reference_id === urlRef.trim());
        if (foundApp) setData(foundApp);
        else setError("Application not found.");
      } else if (dbApps && dbApps.length > 0) {
        setData(dbApps[0]);
      }
    }
    setLoading(false);
  };

  const handleSelectApp = (app) => {
    setData(app);
    setParams({ ref: app.reference_id });
  };

  const handleDelete = async (app, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this application? This action cannot be undone.")) return;
    
    setIsDeleting(true);
    setDeleteId(app.id);

    try {
      if (app.cnic_front_url) {
        const frontPath = app.cnic_front_url.split('/').pop();
        await supabase.storage.from('cnic-documents').remove([frontPath]);
      }
      if (app.cnic_back_url) {
        const backPath = app.cnic_back_url.split('/').pop();
        await supabase.storage.from('cnic-documents').remove([backPath]);
      }

      const { error: dbError } = await supabase
        .from('kyc_applications')
        .delete()
        .eq('id', app.id);

      if (dbError) throw dbError;

      const newApps = applications.filter(a => a.id !== app.id);
      setApplications(newApps);
      
      if (data?.id === app.id) {
        if (newApps.length > 0) handleSelectApp(newApps[0]);
        else { setData(null); setParams({}); }
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete: " + err.message);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };
  
   // ==========================================
  // MODERN PRINT FUNCTION — APEX PREMIUM THEME (WITH STAMPS)
  // ==========================================
  const handlePrintReview = (statusOverride) => {
    if (!data) return;
    setIsGenerating(true);

    const rawStatus = typeof statusOverride === 'string' ? statusOverride : data.status;
    const status = getNormalizedStatus(rawStatus);

    const dateFormatted = new Date(data.created_at).toLocaleString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    const isApproved = status === "Approved";
    const isRejected = status === "Rejected";

    // ── MODERN BRAND THEME ──
    const BRAND       = "#589103";   
    const BRAND_DARK  = "#1B2E01";   
    const BRAND_TINT  = "#F4F8F0";   
    const BRAND_LINE  = "#D1E3B8";   
    
    const GREEN     = "#16A34A";     
    const RED       = "#DC2626";     
    const AMBER     = "#D97706";     

    const stampColor = isApproved ? GREEN : isRejected ? RED : AMBER;
    const stampText  = isApproved ? "APPROVED" : isRejected ? "REJECTED" : "PENDING";
    const amount = `Rs ${Number(data.plan_amount).toLocaleString()}`;

    // ── RESTORED: Detailed Circular Stamp ──
    const stampSVG = `
      <svg width="300" height="300" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" style="transform: rotate(-15deg);">
        <defs>
          <filter id="roughpaper" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="6" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
        <g filter="url(#roughpaper)" fill="none" stroke="${stampColor}">
          <circle cx="250" cy="250" r="220" stroke-width="12" />
          <circle cx="250" cy="250" r="195" stroke-width="4" />
          <circle cx="250" cy="250" r="130" stroke-width="8" />
          <path id="curveTop" d="M 110,250 A 140,140 0 0,1 390,250" fill="none" />
          <text font-family="Arial, sans-serif" font-size="42" font-weight="900" letter-spacing="6" stroke="none" fill="${stampColor}">
            <textPath href="#curveTop" startOffset="50%" text-anchor="middle">${stampText}</textPath>
          </text>
          <path id="curveBottom" d="M 390,250 A 140,140 0 0,1 110,250" fill="none" />
          <text font-family="Arial, sans-serif" font-size="36" font-weight="900" letter-spacing="4" stroke="none" fill="${stampColor}">
            <textPath href="#curveBottom" startOffset="50%" text-anchor="middle">${stampText}</textPath>
          </text>
          <polygon points="100,180 108,200 128,200 112,212 118,232 100,220 82,232 88,212 72,200 92,200" stroke-width="2" />
          <polygon points="400,180 408,200 428,200 412,212 418,232 400,220 382,232 388,212 372,200 392,200" stroke-width="2" />
          <polygon points="100,320 108,300 128,300 112,288 118,268 100,280 82,268 88,288 72,300 92,300" stroke-width="2" />
          <polygon points="400,320 408,300 428,300 412,288 418,268 400,280 382,268 388,288 372,300 392,300" stroke-width="2" />
          <path d="M 50,350 L 450,150 L 450,185 L 50,385 Z" fill="none" stroke-width="4"/>
          <path d="M 50,405 L 450,205 L 450,240 L 50,440 Z" fill="none" stroke-width="4"/>
          <text x="250" y="330" font-family="Georgia, serif" font-size="95" font-weight="900" text-anchor="middle" letter-spacing="8" stroke="none" fill="${stampColor}">${stampText}</text>
        </g>
      </svg>
    `;

    // ── RESTORED: Giant Center Background Stamp ──
    const bigCenterStamp = `
      <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-25deg); opacity: 0.05; pointer-events: none; z-index: 0; display: flex; align-items: center; justify-content: center; width: 100%;">
        <div style="border: 30px solid ${stampColor}; padding: 40px 80px; border-radius: 40px; position: relative;">
           <h1 style="font-size: 200px; font-weight: 900; margin: 0; color: ${stampColor}; font-family: 'Arial Black', sans-serif; letter-spacing: 15px; text-transform: uppercase;">${stampText}</h1>
           ${isRejected ? `<div style="position: absolute; top: 50%; left: -10%; width: 120%; height: 30px; background: ${stampColor}; transform: rotate(15deg);"></div>` : ''}
        </div>
      </div>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Partnership Agreement - ${data.reference_id}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
            @page { margin: 0; size: A4; }
            body { 
              font-family: 'Inter', Arial, sans-serif; 
              color: #333; 
              line-height: 1.6; 
              margin: 0; 
              padding: 0; 
              background: #FFFFFF; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
            }
            .page { 
              position: relative; 
              width: 100%; 
              height: 297mm; 
              page-break-after: always; 
              overflow: hidden; 
              box-sizing: border-box; 
              display: flex; 
              flex-direction: column; 
            }
            .page:last-child { page-break-after: auto; }

            /* ── STAMP CONTAINERS ── */
            .stamp-container-page1 { position: absolute; top: 120px; right: 20px; z-index: 1; opacity: 0.15; }
            .stamp-container-page2 { position: absolute; bottom: 150px; left: 20px; z-index: 1; opacity: 0.15; }

            /* ── MODERN HEADER ── */
            .header { 
              padding: 40px 50px 20px; 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-end; 
              border-bottom: 2px solid ${BRAND};
              position: relative;
              z-index: 10;
            }
            .header-left { display: flex; align-items: center; gap: 15px; }
            .logo-box { 
              width: 40px; height: 40px; 
              background: ${BRAND}; 
              border-radius: 10px; 
              display: flex; align-items: center; justify-content: center; 
              color: #FFF; font-size: 20px; font-weight: 900; 
            }
            .logo-text { font-size: 22px; font-weight: 800; color: ${BRAND_DARK}; letter-spacing: -0.5px; }
            .logo-sub { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: ${BRAND}; margin-top: 2px; }
            
            .header-right { text-align: right; }
            .ref-badge { 
              display: inline-block; 
              background: ${BRAND_TINT}; 
              color: ${BRAND_DARK}; 
              padding: 6px 14px; 
              border-radius: 6px; 
              font-size: 12px; 
              font-weight: 700; 
              border: 1px solid ${BRAND_LINE};
            }
            .conf-text { font-size: 10px; font-weight: 800; color: ${RED}; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 5px; }

            .content { padding: 40px 50px; flex: 1; position: relative; z-index: 10; }

            /* ── TITLE ── */
            .doc-title-wrap { margin-bottom: 35px; }
            .doc-title { font-size: 32px; font-weight: 900; color: ${BRAND_DARK}; margin: 0 0 5px 0; letter-spacing: -1px; }
            .doc-subtitle { font-size: 14px; color: #666; font-weight: 500; margin: 0; }

            /* ── SUMMARY GRID ── */
            .summary-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 1px; 
              background: ${BRAND_LINE}; 
              border: 1px solid ${BRAND_LINE}; 
              border-radius: 12px; 
              overflow: hidden; 
              margin-bottom: 40px; 
            }
            .summary-cell { padding: 20px; background: #FFF; }
            .summary-label { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #666; letter-spacing: 0.5px; display: block; margin-bottom: 6px; }
            .summary-value { font-size: 16px; font-weight: 800; color: ${BRAND_DARK}; }
            .summary-value.status-badge { 
              display: inline-block; 
              padding: 4px 12px; 
              border-radius: 6px; 
              background: ${stampColor}15; 
              color: ${stampColor}; 
              border: 1px solid ${stampColor}30;
              font-size: 14px;
            }

            /* ── SECTIONS ── */
            .section-block { margin-bottom: 30px; }
            .section-title { 
              font-size: 16px; 
              font-weight: 800; 
              color: ${BRAND_DARK}; 
              margin: 0 0 12px 0; 
              display: flex; 
              align-items: center; 
              gap: 10px;
            }
            .section-title::before {
              content: '';
              display: block;
              width: 4px;
              height: 16px;
              background: ${BRAND};
              border-radius: 2px;
            }
            .section-text { font-size: 13.5px; color: #444; line-height: 1.7; margin: 0; }
            
            .highlight-box { 
              background: ${BRAND_TINT}; 
              border-left: 4px solid ${BRAND}; 
              padding: 16px 20px; 
              margin: 20px 0; 
              font-size: 13.5px; 
              color: ${BRAND_DARK}; 
              font-weight: 500; 
              border-radius: 0 8px 8px 0; 
            }

            /* ── MODERN TABLE ── */
            .data-table { 
              width: 100%; 
              border-collapse: separate; 
              border-spacing: 0;
              margin-bottom: 30px; 
              border: 1px solid ${BRAND_LINE}; 
              border-radius: 12px; 
              overflow: hidden;
            }
            .data-table th { 
              background: ${BRAND_TINT}; 
              text-align: left; 
              padding: 14px 20px; 
              font-size: 12px; 
              font-weight: 800; 
              text-transform: uppercase; 
              color: ${BRAND_DARK}; 
              border-bottom: 1px solid ${BRAND_LINE}; 
              letter-spacing: 0.5px;
            }
            .data-table td { 
              padding: 16px 20px; 
              font-size: 13.5px; 
              border-bottom: 1px solid ${BRAND_LINE}; 
              color: #444; 
              font-weight: 500;
            }
            .data-table tr:last-child td { border-bottom: none; }
            .data-table tr:nth-child(even) td { background: #FAFCF8; }

            /* ── SIGNATURES ── */
            .signature-area { display: flex; justify-content: space-between; margin-top: 60px; padding-top: 20px; }
            .sig-block { width: 45%; }
            .sig-line { border-top: 1.5px solid ${BRAND_DARK}; padding-top: 10px; font-size: 12px; font-weight: 700; color: ${BRAND_DARK}; margin-top: 40px; }

            /* ── FOOTER ── */
            .footer { 
              position: absolute; 
              bottom: 0; left: 0; right: 0; 
              padding: 15px 50px; 
              background: ${BRAND_TINT};
              border-top: 1px solid ${BRAND_LINE};
              display: flex; 
              justify-content: space-between; 
              font-size: 11px; 
              font-weight: 600;
              color: ${BRAND_DARK}; 
              z-index: 10; 
            }
          </style>
        </head>
        <body>
          <!-- PAGE 1 -->
          <div class="page">
            ${bigCenterStamp}
            <div class="header">
              <div class="header-left">
                <div class="logo-box">A</div>
                <div>
                  <div class="logo-text">ApexDeal</div>
                  <div class="logo-sub">Shirkat Fund Partnership</div>
                </div>
              </div>
              <div class="header-right">
                <div class="conf-text">CONFIDENTIAL</div>
                <div class="ref-badge">Ref: ${data.reference_id}</div>
              </div>
            </div>
            
            <div class="content">
              <div class="stamp-container-page1">${stampSVG}</div>
              
              <div class="doc-title-wrap">
                <h1 class="doc-title">Partnership Agreement</h1>
                <p class="doc-subtitle">Official Terms & Policies of the Shirkat Fund</p>
              </div>

              <div class="summary-grid">
                <div class="summary-cell">
                  <span class="summary-label">Application Status</span>
                  <span class="summary-value status-badge">${status}</span>
                </div>
                <div class="summary-cell">
                  <span class="summary-label">${isApproved ? 'Approved Capital' : 'Requested Capital'}</span>
                  <span class="summary-value">${amount}</span>
                </div>
                <div class="summary-cell">
                  <span class="summary-label">Model</span>
                  <span class="summary-value">Zero-Interest Profit Share</span>
                </div>
                <div class="summary-cell">
                  <span class="summary-label">Applicant Name</span>
                  <span class="summary-value">${data.full_name}</span>
                </div>
                <div class="summary-cell">
                  <span class="summary-label">${isApproved ? 'Approval Date' : 'Review Date'}</span>
                  <span class="summary-value">${dateFormatted}</span>
                </div>
                <div class="summary-cell">
                  <span class="summary-label">Current Tier</span>
                  <span class="summary-value">Level 1 (Starter)</span>
                </div>
              </div>

              <div class="section-block">
                <h2 class="section-title">1. Zero-Interest Capital Disbursement</h2>
                <p class="section-text">Shirkat Fund operates on a strict zero-interest model. Once your application is approved, your requested capital (${amount}) will be disbursed directly to your designated account. This capital is provided to help you start, sustain, or scale your business without the burden of traditional interest-based loans.</p>
              </div>

              <div class="section-block">
                <h2 class="section-title">2. Profit Sharing & Payment Discipline</h2>
                <p class="section-text">Unlike traditional loans, Shirkat Fund operates on a flexible profit-sharing model. You are required to pay a small, agreed-upon profit share (daily or monthly) based on your business cash flow.</p>
                <div class="highlight-box">
                  <strong>Key Discipline:</strong> Maintaining strict discipline with your payments is the foundation of your partnership with Apex. Late payments may affect your AI Trust Score.
                </div>
              </div>

              <div class="section-block">
                <h2 class="section-title">3. Apex Discipline & Trust Tracking</h2>
                <p class="section-text">The Apex Project utilizes an advanced AI system to track your financial discipline and trust score. Every payment you make on time builds your history and increases your trust score. This score is directly linked to your future capital limits and partnership tier.</p>
              </div>
            </div>
            
            <div class="footer">
              <span>System-generated policy document. Electronically binding upon acceptance of funds.</span>
              <span>Page 1 of 2</span>
            </div>
          </div>

          <!-- PAGE 2 -->
          <div class="page">
            ${bigCenterStamp}
            <div class="header">
              <div class="header-left">
                <div class="logo-box">A</div>
                <div>
                  <div class="logo-text">ApexDeal</div>
                  <div class="logo-sub">Shirkat Fund Partnership</div>
                </div>
              </div>
              <div class="header-right">
                <div class="conf-text">CONFIDENTIAL</div>
                <div class="ref-badge">Ref: ${data.reference_id}</div>
              </div>
            </div>

            <div class="content">
              <div class="stamp-container-page2">${stampSVG}</div>

              <div class="section-block">
                <h2 class="section-title">4. Building Your History & Unlocking Next Levels</h2>
                <p class="section-text">Your journey with Shirkat Fund does not end with this capital. By successfully completing your payments and adhering to the Apex policy, you build a verified financial history. This history will unlock the following tiers:</p>
              </div>

              <table class="data-table">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Tier</th>
                    <th>Benefit</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Level 1</strong></td>
                    <td>Starter</td>
                    <td>20k – 60k Capital</td>
                    <td style="color: ${BRAND}; font-weight: 700;">Current</td>
                  </tr>
                  <tr>
                    <td><strong>Level 2</strong></td>
                    <td>Growth</td>
                    <td>1 Lakh to 5 Lakh Business Startup</td>
                    <td style="color: #888;">Locked</td>
                  </tr>
                  <tr>
                    <td><strong>Level 3</strong></td>
                    <td>Apex Verified</td>
                    <td>Official Apex Verified Partner Badge</td>
                    <td style="color: #888;">Locked</td>
                  </tr>
                  <tr>
                    <td><strong>Level 4</strong></td>
                    <td>Premium</td>
                    <td>10 Lakh to 30 Lakh Premium Startup</td>
                    <td style="color: #888;">Locked</td>
                  </tr>
                </tbody>
              </table>

              <div class="section-block">
                <h2 class="section-title">5. Partner of the Apex Team</h2>
                <p class="section-text">Once you have successfully completed 2 levels and maintained a high trust score, you are eligible to receive the Apex Verified Badge. Upon receiving this badge and completing the required Apex policy, you will be officially recognized as a <strong>Partner of the Apex Team</strong>.</p>
                <p class="section-text" style="margin-top: 10px;">As a partner, you will receive the Apex Verified Badge on your profile, a grand reward of up to 1 Lakh Rupees, and access to exclusive premium startup funds. You are no longer just a vendor; you are a trusted financial partner.</p>
              </div>

              <div class="section-block">
                <h2 class="section-title">6. Legal & Compliance Notice</h2>
                <p class="section-text">By accepting this capital, you agree to abide by the rules and regulations of Shirkat Fund. Any attempt to defraud the system, provide fake documents, or intentionally default on payments will result in immediate termination of the agreement, legal action, and a permanent ban from the Apex network.</p>
                <p class="section-text" style="margin-top: 10px;">This is a system-generated policy document. By accepting the funds, you electronically agree to these terms.</p>
              </div>

              <div class="signature-area">
                <div class="sig-block">
                  <div class="sig-line">Partner signature / electronic acceptance</div>
                </div>
                <div class="sig-block">
                  <div class="sig-line">Authorised officer, Shirkat Fund</div>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <span>System-generated policy document. Electronically binding upon acceptance of funds.</span>
              <span>Page 2 of 2</span>
            </div>
          </div>
        </body>
      </html>
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(htmlContent);
    doc.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setIsGenerating(false);
      }, 500);
    };

    setTimeout(() => {
      if (document.body.contains(iframe)) iframe.remove();
    }, 60000);
  };
  const filteredApps = useMemo(() => {
    if (activeFilter === "All") return applications;
    return applications.filter(app => getNormalizedStatus(app.status) === activeFilter);
  }, [applications, activeFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, color: C.text }}>
        <FaSpinner className="animate-spin text-4xl" style={{ color: C.primary }} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: C.bg, color: C.text, fontFamily: "Inter, sans-serif" }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-50 text-red-500">
            <FaTimesCircle size={28} />
          </div>
          <h2 className="text-xl font-bold mb-2">Oops!</h2>
          <p className="text-sm mb-6" style={{ color: C.muted }}>{error}</p>
          <button onClick={() => navigate("/shirkat-fund")} className="px-6 py-2 rounded-lg font-bold text-white transition hover:opacity-90" style={{ background: C.primary }}>
            Back to Shirkat Fund
          </button>
        </div>
      </div>
    );
  }

  const activeStatus = data ? getNormalizedStatus(data.status) : null;

  return (
    <div className="min-h-screen pb-20 lg:pb-0" style={{ background: C.bg, color: C.text, fontFamily: "Inter, sans-serif" }}>
      
      {/* TOP TABS */}
      <div className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-30" style={{ borderColor: C.line }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 py-4 sm:py-0 sm:h-16">
      <button 
  onClick={() => navigate("/shirkat-fund")} 
  className="flex items-center gap-2 text-sm font-semibold transition hover:opacity-70 self-start sm:self-auto"
  style={{ color: C.muted }}
>
  <FaArrowLeft size={14} /> Back to Home
</button>
            <div className="flex h-full gap-6 sm:gap-8 overflow-x-auto pb-1 sm:pb-0 hide-scrollbar">
              <div className="flex items-center gap-2 whitespace-nowrap h-full sm:border-b-2 text-sm font-semibold py-2 sm:py-0" style={{ borderColor: C.text, color: C.text }}>
                Applied Apps <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: C.line, color: C.muted }}>{applications.length}</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap h-full sm:border-b-2 border-transparent text-sm font-medium opacity-60 py-2 sm:py-0">
                Saved Items <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: C.line, color: C.muted }}>0</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
        
        {/* LEFT SIDEBAR */}
        <div className="w-full lg:w-[220px] flex-shrink-0">
          <div className="lg:sticky lg:top-24 flex flex-row lg:flex-col flex-wrap gap-2 pb-2 lg:pb-0">
            {["All", "Submitted", "Shortlisted", "Approved", "Rejected"].map((filter) => {
              const count = filter === "All" 
                ? applications.length 
                : applications.filter(a => getNormalizedStatus(a.status) === filter).length;
              const isActive = activeFilter === filter;

              return (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`flex-1 lg:flex-none flex items-center justify-center lg:justify-between gap-2 px-3 py-2 lg:py-3 rounded-xl text-xs sm:text-sm font-semibold transition ${isActive ? 'text-black' : 'hover:bg-black/5 border border-gray-200 lg:border-transparent'}`}
                  style={{ 
                    background: isActive ? C.ok : 'transparent', 
                    color: isActive ? '#000' : C.text,
                    borderColor: isActive ? 'transparent' : C.line
                  }}
                >
                  <span className="whitespace-nowrap">{filter}</span>
                  {filter === "All" && <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-black/10' : ''}`} style={{ background: isActive ? 'transparent' : C.line }}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* MIDDLE LIST */}
        <div className="w-full lg:w-[380px] flex-shrink-0">
          <div className="space-y-4 lg:max-h-[calc(100vh-140px)] overflow-y-auto lg:pr-2 pb-6 custom-scrollbar">
            {filteredApps.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border" style={{ borderColor: C.line }}>
                <FaFileAlt className="mx-auto text-3xl mb-3" style={{ color: C.primaryLight }} />
                <p className="text-sm font-semibold" style={{ color: C.muted }}>No applications found</p>
              </div>
            ) : (
              filteredApps.map((app) => {
                const isSelected = data?.id === app.id;
                const appStatus = getNormalizedStatus(app.status);
                const isAppApproved = appStatus === 'Approved';
                const isAppRejected = appStatus === 'Rejected';
                
                return (
                  <div 
                    key={app.id}
                    onClick={() => handleSelectApp(app)}
                    className={`relative p-4 sm:p-5 rounded-2xl cursor-pointer transition-all duration-200 border-2 ${isSelected ? '' : 'hover:border-gray-300'}`}
                    style={{ 
                      background: isSelected ? 'linear-gradient(to bottom right, #ffffff, #fffbeb)' : C.card,
                      borderColor: isSelected ? C.ok : C.line
                    }}
                  >
                    <div className="flex items-start gap-3 sm:gap-4 mb-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: C.primaryLight, color: C.primary }}>
                        <FaWallet size={18} className="sm:w-[22px] sm:h-[22px]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm sm:text-[15px] leading-tight" style={{ color: C.text }}>Capital Application</h3>
                        <p className="text-[11px] sm:text-xs font-semibold mt-0.5" style={{ color: C.muted }}>Ref: {app.reference_id}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-semibold border" style={{ borderColor: C.line, color: C.muted }}>
                        {app.city || "Pakistan"}
                      </span>
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-semibold border" style={{ borderColor: C.line, color: C.muted }}>
                        Rs {Number(app.plan_amount).toLocaleString()}
                      </span>
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-[10px] sm:text-xs font-semibold border" style={{ borderColor: C.line, color: C.muted }}>
                        {app.payment_frequency || "Monthly"}
                      </span>
                    </div>

                    <p className="text-xs sm:text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: C.muted }}>
                      Join our dynamic team to design user-centric products that impact millions...
                    </p>

                    <div className="flex flex-wrap items-center justify-between pt-3 border-t gap-2 mt-auto" style={{ borderColor: C.line }}>
                      <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs font-semibold" style={{ color: isAppApproved ? C.ok : isAppRejected ? C.danger : C.pending }}>
                        {isAppApproved ? <FaCheckCircle size={12} /> : isAppRejected ? <FaTimesCircle size={12} /> : <FaClock size={12} />}
                        {appStatus}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => handleDelete(app, e)}
                          disabled={isDeleting && deleteId === app.id}
                          className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
                        >
                          {isDeleting && deleteId === app.id ? <FaSpinner className="animate-spin" size={12}/> : <FaTrash size={12} />}
                        </button>
                        <button className="px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-bold text-black transition hover:opacity-90" style={{ background: C.ok }}>
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANE */}
        <div className="flex-1 w-full min-w-0">
          {data ? (
            <div className="bg-white rounded-2xl border min-h-[500px] flex flex-col" style={{ borderColor: C.line }}>
              <div className="p-4 sm:p-6 lg:p-8 border-b relative" style={{ borderColor: C.line, background: 'linear-gradient(to bottom right, #ffffff, #fffbeb)' }}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5 sm:mb-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-white border" style={{ borderColor: C.line }}>
                      <FaWallet size={22} className="sm:w-[26px] sm:h-[26px]" style={{ color: C.primary }} />
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold mb-1 leading-tight" style={{ color: C.text }}>Personal Capital</h1>
                      <p className="text-xs sm:text-sm font-medium" style={{ color: C.muted }}>Shirkat Fund • Apex Partner</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <button onClick={() => handleDelete(data)} className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border font-semibold text-xs sm:text-sm transition hover:bg-gray-50 flex items-center gap-2" style={{ borderColor: C.line, color: C.muted }}>
                      <FaTrash size={12} /> Delete
                    </button>
                    <div 
                      className="px-4 py-1.5 sm:px-5 sm:py-2 rounded-lg font-bold text-xs sm:text-sm text-black" 
                      style={{ background: activeStatus === 'Rejected' ? C.danger : activeStatus === 'Approved' ? C.ok : C.pending }}
                    >
                      {activeStatus}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3 mb-5 sm:mb-6">
                  <span className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border text-xs sm:text-sm font-semibold bg-white" style={{ borderColor: C.line, color: C.muted }}>
                    <FaMapMarkerAlt size={12}/> {data.city || "Remote"}
                  </span>
                  <span className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border text-xs sm:text-sm font-semibold bg-white" style={{ borderColor: C.line, color: C.muted }}>
                    <FaWallet size={12}/> Rs {Number(data.plan_amount).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border text-xs sm:text-sm font-semibold bg-white" style={{ borderColor: C.line, color: C.muted }}>
                    <FaCalendarAlt size={12}/> {data.payment_frequency || "Monthly"}
                  </span>
                  <span className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border text-[11px] sm:text-sm font-bold bg-purple-50 text-purple-700 border-purple-200 ml-auto mt-2 sm:mt-0">
                    <FaCheckCircle size={12}/> 95% Match
                  </span>
                </div>

                <p className="text-xs sm:text-sm font-semibold" style={{ color: C.muted }}>
                  Applied: {new Date(data.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              <div className="px-4 sm:px-8 py-8 sm:py-10 border-b overflow-x-auto hide-scrollbar" style={{ borderColor: C.line }}>
                <div className="relative flex justify-between items-start min-w-[500px] sm:min-w-0">
                  <div className="absolute top-4 left-0 w-full h-1 bg-gray-100 rounded-full -z-0"></div>
                  <div className="absolute top-4 left-0 h-1 rounded-full -z-0 transition-all duration-1000" 
                       style={{ 
                         background: C.ok, 
                         width: activeStatus === 'Approved' ? '100%' : activeStatus === 'Rejected' ? '100%' : activeStatus === 'Shortlisted' ? '50%' : '25%' 
                       }}>
                  </div>

                  <div className="relative z-10 flex flex-col items-center w-1/4 text-center px-1">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center mb-3 text-black" style={{ background: C.ok }}>
                      <FaCheck size={12} className="sm:w-3.5 sm:h-3.5" />
                    </div>
                    <h4 className="text-xs sm:text-sm font-bold mb-1" style={{ color: C.text }}>Submitted</h4>
                    <p className="text-[10px] sm:text-xs leading-tight px-1 sm:px-2" style={{ color: C.muted }}>Application successfully submitted</p>
                  </div>

                  <div className="relative z-10 flex flex-col items-center w-1/4 text-center px-1">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center mb-3 text-black" 
                         style={{ background: activeStatus === 'Approved' || activeStatus === 'Rejected' ? C.ok : activeStatus === 'Shortlisted' ? C.ok : C.line }}>
                      {activeStatus === 'Approved' || activeStatus === 'Rejected' ? <FaCheck size={12} className="sm:w-3.5 sm:h-3.5" /> : <FaClock size={12} className="sm:w-3.5 sm:h-3.5" />}
                    </div>
                    <h4 className="text-xs sm:text-sm font-bold mb-1" style={{ color: activeStatus === 'Shortlisted' ? C.ok : C.text }}>Shortlisted</h4>
                    <p className="text-[10px] sm:text-xs leading-tight px-1 sm:px-2" style={{ color: activeStatus === 'Shortlisted' ? C.ok : C.muted }}>
                      {activeStatus === 'Shortlisted' ? "Review Pending..." : "Shortlisted for the next step"}
                    </p>
                  </div>

                  <div className="relative z-10 flex flex-col items-center w-1/4 text-center px-1">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center mb-3 border-2 bg-white" 
                         style={{ borderColor: activeStatus === 'Approved' ? C.ok : C.line, color: activeStatus === 'Approved' ? C.ok : C.line }}>
                      <FaCheck size={12} className="sm:w-3.5 sm:h-3.5" />
                    </div>
                    <h4 className="text-xs sm:text-sm font-bold mb-1" style={{ color: activeStatus === 'Approved' ? C.text : C.muted }}>Approved</h4>
                    <p className="text-[10px] sm:text-xs leading-tight px-1 sm:px-2" style={{ color: C.muted }}>Capital approved for release</p>
                  </div>

                  <div className="relative z-10 flex flex-col items-center w-1/4 text-center px-1">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center mb-3 border-2 bg-white" 
                         style={{ borderColor: activeStatus === 'Rejected' ? C.danger : C.line, color: activeStatus === 'Rejected' ? C.danger : C.line }}>
                      <FaTimes size={12} className="sm:w-3.5 sm:h-3.5" />
                    </div>
                    <h4 className="text-xs sm:text-sm font-bold mb-1" style={{ color: activeStatus === 'Rejected' ? C.danger : C.muted }}>Rejected</h4>
                    <p className="text-[10px] sm:text-xs leading-tight px-1 sm:px-2" style={{ color: C.muted }}>Application closed</p>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 flex-1">
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3" style={{ color: C.text }}>Application Summary</h3>
                  <p className="text-xs sm:text-sm leading-relaxed" style={{ color: C.muted }}>
                    You have applied for a zero-interest capital funding of <strong style={{ color: C.text }}>Rs {Number(data.plan_amount).toLocaleString()}</strong>. 
                    This funding is designed to help small business owners scale their operations without the burden of daily interest. 
                    Our AI trust-tracking system will monitor your payment discipline to unlock higher tiers.
                  </p>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3" style={{ color: C.text }}>Applicant Details</h3>
                  <ul className="space-y-2 text-xs sm:text-sm" style={{ color: C.muted }}>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></div> <strong>Name:</strong> {data.full_name}</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></div> <strong>City:</strong> {data.city}</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></div> <strong>WhatsApp:</strong> {data.phone || "N/A"}</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></div> <strong>Payment Model:</strong> {data.payment_frequency || "Monthly"}</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3" style={{ color: C.text }}>Submitted Documents</h3>
                  <div className="flex gap-4 sm:gap-6">
                    {data.cnic_front_url && (
                      <a href={data.cnic_front_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group">
                        <div className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden border-2 transition group-hover:border-blue-500" style={{ borderColor: C.line }}>
                          <img src={data.cnic_front_url} alt="CNIC Front" className="w-full h-full object-cover" />
                        </div>
                        <span className="text-[10px] sm:text-xs font-semibold group-hover:underline" style={{ color: C.link }}>View Front</span>
                      </a>
                    )}
                    {data.cnic_back_url && (
                      <a href={data.cnic_back_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group">
                        <div className="w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden border-2 transition group-hover:border-blue-500" style={{ borderColor: C.line }}>
                          <img src={data.cnic_back_url} alt="CNIC Back" className="w-full h-full object-cover" />
                        </div>
                        <span className="text-[10px] sm:text-xs font-semibold group-hover:underline" style={{ color: C.link }}>View Back</span>
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3" style={{ color: C.text }}>Benefits</h3>
                  <ul className="space-y-2 text-xs sm:text-sm" style={{ color: C.muted }}>
                    <li className="flex items-center gap-2"><FaCheck size={12} style={{ color: C.ok }}/> Zero Interest Capital</li>
                    <li className="flex items-center gap-2"><FaCheck size={12} style={{ color: C.ok }}/> Flexible Profit Sharing</li>
                    <li className="flex items-center gap-2"><FaCheck size={12} style={{ color: C.ok }}/> Fast CNIC Verification</li>
                    <li className="flex items-center gap-2"><FaCheck size={12} style={{ color: C.ok }}/> Unlock Apex Verified Badge</li>
                  </ul>
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t bg-gray-50 rounded-b-2xl mt-auto" style={{ borderColor: C.line }}>
                <button 
                  onClick={handlePrintReview} 
                  disabled={isGenerating}
                  className="w-full flex items-center justify-center gap-2 py-3 sm:py-4 rounded-xl font-bold text-sm sm:text-lg transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-black"
                  style={{ background: C.ok }}
                >
                  {isGenerating ? (
                    <><FaSpinner className="animate-spin" /> Preparing Document...</>
                  ) : (
                    <><FaPrint /> Print / Save Application Document</>
                  )}
                </button>
                <p className="text-center text-[10px] sm:text-xs mt-3 font-medium" style={{ color: C.muted }}>
                  Opens your browser's print dialog. Select "Save as PDF" to download.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[400px] lg:min-h-[600px] border-2 border-dashed rounded-2xl flex flex-col items-center justify-center text-center p-8" style={{ borderColor: C.line }}>
              <FaFileAlt size={48} className="mb-4 opacity-20" />
              <h2 className="text-xl font-bold mb-2">No Application Selected</h2>
              <p className="text-sm max-w-sm" style={{ color: C.muted }}>
                Select an application from the list to view its details and track its progress.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}